import { useState, useEffect } from 'react';
import { Property, PropertyFilters } from '../types/property.types';
import { propertyService } from '../services/propertyService';

interface UsePropertiesReturn {
  properties: Property[];
  loading: boolean;
  error: string | null;
  loadProperties: (filters?: PropertyFilters) => Promise<void>;
  createProperty: (property: Omit<Property, 'id'>) => Promise<Property>;
  updateProperty: (id: string, updates: Partial<Property>) => Promise<Property>;
  deleteProperty: (id: string) => Promise<void>;
}

export const useProperties = (): UsePropertiesReturn => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProperties = async (filters?: PropertyFilters) => {
    setLoading(true);
    setError(null);
    try {
      const data = await propertyService.getAll(filters);
      setProperties(data.propiedades || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading properties');
    } finally {
      setLoading(false);
    }
  };

  const createProperty = async (property: Omit<Property, 'id'>): Promise<Property> => {
    const newProperty = await propertyService.create(property);
    setProperties(prev => [...prev, newProperty]);
    return newProperty;
  };

  const updateProperty = async (id: string, updates: Partial<Property>): Promise<Property> => {
    const updatedProperty = await propertyService.update(id, updates);
    setProperties(prev => 
      prev.map(p => p.id === id ? updatedProperty : p)
    );
    return updatedProperty;
  };

  const deleteProperty = async (id: string): Promise<void> => {
    await propertyService.delete(id);
    setProperties(prev => prev.filter(p => p.id !== id));
  };

  useEffect(() => {
    loadProperties();
  }, []);

  return {
    properties,
    loading,
    error,
    loadProperties,
    createProperty,
    updateProperty,
    deleteProperty,
  };
};